import { useState, useEffect, useCallback } from 'react'
import { Outlet, Navigate, useNavigate, useLocation } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import api from '@/shared/api/client'
import { authApi } from '@/features/auth/api/auth.api'
import { useAuthStore } from '@/shared/stores/auth.store'
import { resolveHomeRoute } from '@/shared/rbac'
import { prefetchHomeData } from '@/features/auth/lib/prefetch-home'

export interface AuthLayoutContext {
  onLoginSuccess: (userName: string, homeRoute: string) => void
}

type Phase = 'idle' | 'check' | 'slide' | 'expand' | 'exit'

/**
 * Read a post-login redirect from `?next=` and only allow same-origin paths
 * (must start with `/` and not `//` or `/\`) to avoid open-redirect abuse.
 * Also rejects bouncing back into `/auth/*` which would loop the user.
 */
function getSafeNext(search: string): string | null {
  const params = new URLSearchParams(search)
  const raw = params.get('next')
  if (!raw) return null
  if (!raw.startsWith('/') || raw.startsWith('//') || raw.startsWith('/\\')) return null
  if (raw.startsWith('/auth/')) return null
  return raw
}

interface McpLoginParams {
  redirect_uri: string
  state: string
  code_challenge: string
}

/**
 * When the login page is opened by an MCP client it carries
 * `?mcp_login=1&redirect_uri=…&state=…&code_challenge=…`. The redirect_uri
 * is allow-listed — loopback http URLs (CLI clients) plus exact-match
 * hosted-consumer callbacks (claude.ai etc.). PKCE alone does NOT protect
 * the code against a login-CSRF / auth-code-interception attack where the
 * attacker controls the code_challenge, so we mirror the BE allowlist here
 * to hide the UI from a phishing link entirely. Keep in lockstep with
 * TRUSTED_MCP_REDIRECT_URIS in src/routes/auth.routes.ts (BE).
 */
const TRUSTED_MCP_REDIRECT_URIS = [
  'https://claude.ai/api/mcp/auth_callback',
] as const

function isAllowedMcpRedirectUri(value: string): boolean {
  try {
    const u = new URL(value)
    if (
      u.protocol === 'http:' &&
      (u.hostname === '127.0.0.1' || u.hostname === 'localhost' || u.hostname === '[::1]')
    ) {
      return true
    }
    return (TRUSTED_MCP_REDIRECT_URIS as readonly string[]).includes(value)
  } catch {
    return false
  }
}

function getMcpParams(search: string): McpLoginParams | null {
  const params = new URLSearchParams(search)
  if (params.get('mcp_login') !== '1') return null
  const redirect_uri = params.get('redirect_uri') ?? ''
  const state = params.get('state') ?? ''
  const code_challenge = params.get('code_challenge') ?? ''
  if (!state || !code_challenge) return null
  if (!isAllowedMcpRedirectUri(redirect_uri)) return null
  return { redirect_uri, state, code_challenge }
}

/** Mint the one-time code and hand the browser back to the local MCP listener. */
async function connectMcp(mcp: McpLoginParams): Promise<void> {
  const { code } = await authApi.mcpAuthorize({
    redirect_uri: mcp.redirect_uri,
    state: mcp.state,
    code_challenge: mcp.code_challenge,
  })
  const url = new URL(mcp.redirect_uri)
  url.searchParams.set('code', code)
  url.searchParams.set('state', mcp.state)
  window.location.href = url.toString()
}

export function AuthLayout() {
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const { user, isAuthenticated, isLoading } = useAuthStore()

  const [phase, setPhase] = useState<Phase>('idle')
  const [transitionName, setTransitionName] = useState('')
  const [specialtiesCount, setSpecialtiesCount] = useState<number | null>(null)

  useEffect(() => {
    api.get<{ specialties_count: number }>('/stats')
      .then((r) => setSpecialtiesCount(r.data.specialties_count))
      .catch(() => {})
  }, [])

  const onLoginSuccess = useCallback((userName: string, homeRoute: string) => {
    // MCP browser-login: instead of entering the app, hand a one-time code back
    // to the local MCP client and stop here.
    const mcp = getMcpParams(location.search)
    if (mcp) {
      setTransitionName(userName)
      setPhase('check')
      connectMcp(mcp).catch(() => {
        navigate(getSafeNext(location.search) ?? homeRoute, { replace: true })
      })
      return
    }
    const destination = getSafeNext(location.search) ?? homeRoute
    setTransitionName(userName)
    prefetchHomeData(queryClient, destination)
    setPhase('check')
    setTimeout(() => setPhase('slide'), 800)
    setTimeout(() => setPhase('exit'), 1600)
    setTimeout(() => navigate(destination, { replace: true }), 2200)
  }, [navigate, queryClient, location.search])

  if (isLoading) return null
  if (isAuthenticated && phase === 'idle') {
    // Already logged in + opened by the MCP → skip straight to the handoff.
    const mcp = getMcpParams(location.search)
    if (mcp) return <McpConnecting mcp={mcp} />
    const destination = getSafeNext(location.search) ?? resolveHomeRoute(user?.permissions ?? [])
    return <Navigate to={destination} replace />
  }

  const isTransitioning = phase !== 'idle'

  return (
    <>
      <style>{`
        @keyframes authPanelSlideOut {
          from { transform: translateX(0); opacity: 1; }
          to { transform: translateX(-100%); opacity: 0; }
        }
        @keyframes authCheckGrow {
          from { transform: scale(0); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        @keyframes authCheckDraw {
          from { stroke-dashoffset: 48; }
          to { stroke-dashoffset: 0; }
        }
        @keyframes authPulseRing {
          0% { box-shadow: 0 0 0 0 rgba(25, 80, 198, 0.3); }
          70% { box-shadow: 0 0 0 24px rgba(25, 80, 198, 0); }
          100% { box-shadow: 0 0 0 0 rgba(25, 80, 198, 0); }
        }
        @keyframes authTextUp {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes authBarFill {
          from { transform: scaleX(0); }
          to { transform: scaleX(1); }
        }
        @keyframes authFadeOut {
          from { opacity: 1; transform: scale(1); }
          to { opacity: 0; transform: scale(0.98); }
        }
        @keyframes authGlideCenter {
          from { transform: translateX(0); opacity: 1; }
          to { transform: translateX(-25vw); opacity: 1; }
        }
        @keyframes authGlideFade {
          from { transform: translateX(-25vw); opacity: 1; }
          to { transform: translateX(-25vw); opacity: 0; }
        }
        @keyframes authFormFadeOut {
          from { opacity: 1; transform: translateY(0); }
          to { opacity: 0; transform: translateY(-20px); }
        }
      `}</style>

      <div
        className="auth-shell"
        style={{
          display: 'flex',
          minHeight: '100vh',
          position: 'relative',
          animation: phase === 'exit' ? 'authFadeOut 0.6s ease-in forwards' : undefined,
        }}
      >
        {/* ── Left Branding Panel ── */}
        <div
          style={{
            flex: '0 0 50%',
            background: 'linear-gradient(160deg, #0e266c 0%, #1950C6 45%, #4876db 100%)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            padding: '48px 56px',
            position: 'relative',
            overflow: 'hidden',
            // Slide out animation
            ...(phase === 'slide' || phase === 'expand' || phase === 'exit'
              ? {
                  animation: 'authPanelSlideOut 0.6s cubic-bezier(0.4, 0, 0.2, 1) forwards',
                }
              : {}),
          }}
        >
          {/* Subtle grid pattern overlay */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              backgroundImage:
                'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.04) 1px, transparent 0)',
              backgroundSize: '32px 32px',
              pointerEvents: 'none',
            }}
          />

          {/* Logo */}
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 80 }}>
              <img
                src="/brand/medicodio-logo-white.png"
                alt="Medicodio"
                style={{ height: 32, width: 'auto', display: 'block' }}
              />
            </div>

            <h1
              style={{
                fontSize: 44,
                fontWeight: 800,
                color: '#ffffff',
                lineHeight: 1.1,
                letterSpacing: '-0.03em',
                fontFamily: 'var(--font-sans)',
                maxWidth: 440,
                marginBottom: 20,
              }}
            >
              where AI coding delivers
              <br />
              clinical precision
            </h1>
            <p
              style={{
                fontSize: 16,
                lineHeight: 1.7,
                color: 'rgba(255,255,255,0.7)',
                maxWidth: 420,
                fontFamily: 'var(--font-sans)',
              }}
            >
              An AI-native platform that transforms clinical documentation into
              accurate medical codes — with full transparency, structured learning,
              and a clear path to coding autonomy.
            </p>
          </div>

          {/* Stats strip */}
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div
              style={{
                display: 'flex',
                gap: 40,
                marginBottom: 40,
                paddingTop: 24,
                borderTop: '1px solid rgba(255,255,255,0.12)',
              }}
            >
              {[
                { value: specialtiesCount !== null ? String(specialtiesCount) : '—', label: 'Specialties' },
                { value: "100K+", label: 'Charts Processed' },
                { value: '100%', label: 'HIPAA Compliant' },
              ].map((stat) => (
                <div key={stat.label}>
                  <div
                    style={{
                      fontSize: 28,
                      fontWeight: 800,
                      color: '#ffffff',
                      fontFamily: 'var(--font-sans)',
                      letterSpacing: '-0.02em',
                    }}
                  >
                    {stat.value}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 500,
                      color: 'rgba(255,255,255,0.5)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      marginTop: 2,
                    }}
                  >
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>

            {/* Testimonial */}
            <div
              style={{
                padding: '20px 24px',
                borderRadius: 16,
                background: 'rgba(255,255,255,0.06)',
                backdropFilter: 'blur(8px)',
                border: '1px solid rgba(255,255,255,0.08)',
              }}
            >
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: 'rgba(255,255,255,0.9)',
                  lineHeight: 1.5,
                  marginBottom: 12,
                  fontFamily: 'var(--font-sans)',
                }}
              >
                "Every encounter is traceable from ingestion to write-back.
                Transparency is not an afterthought — it's the foundation."
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: 'rgba(255,255,255,0.4)',
                  fontFamily: 'var(--font-sans)',
                }}
              >
                Product Philosophy — Medicodio Platform
              </div>
            </div>
          </div>

          {/* Copyright */}
          <div
            style={{
              position: 'relative',
              zIndex: 1,
              textAlign: 'center',
              paddingTop: 24,
              fontSize: 12,
              color: 'rgba(255,255,255,0.35)',
              fontFamily: 'var(--font-sans)',
              letterSpacing: '0.01em',
            }}
          >
            &copy; {new Date().getFullYear()} Medicodio. All rights reserved.
          </div>
        </div>

        {/* ── Right Form Panel ── */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 'var(--sp-6)',
            background: 'var(--bg)',
            transition: 'flex 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
            // Scroll only for the form (which can be tall — mfa-setup).
            // SuccessContent animation needs visible overflow for its
            // slide/expand transform, so we gate scroll on !isTransitioning.
            overflowY: isTransitioning ? 'visible' : 'auto',
            maxHeight: '100vh',
          }}
        >
          <div style={{ maxWidth: 420, width: '100%', margin: isTransitioning ? undefined : 'auto 0' }}>
            {/* When transitioning, show success UI instead of form */}
            {isTransitioning ? (
              <SuccessContent
                name={transitionName}
                phase={phase}
              />
            ) : (
              <Outlet context={{ onLoginSuccess } satisfies AuthLayoutContext} />
            )}
          </div>
        </div>
      </div>
    </>
  )
}

// ── MCP connecting screen (already-authenticated handoff) ──

function McpConnecting({ mcp }: { mcp: McpLoginParams }) {
  const [error, setError] = useState('')
  useEffect(() => {
    connectMcp(mcp).catch((e) => setError(e instanceof Error ? e.message : 'Could not connect to the local MCP client.'))
  }, [mcp])
  return (
    <div
      className="auth-shell"
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--bg)',
        padding: 'var(--sp-6)',
      }}
    >
      <div style={{ maxWidth: 420, textAlign: 'center' }}>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>
          {error ? 'Connection failed' : 'Connecting Claude Code…'}
        </h2>
        <p style={{ fontSize: 14, color: error ? 'var(--danger)' : 'var(--text-muted)', marginTop: 8 }}>
          {error
            ? error
            : 'Handing your session back to the MCP running on this machine. You can close this tab once it completes.'}
        </p>
      </div>
    </div>
  )
}

// ── Success animation content ──

function SuccessContent({ name, phase }: { name: string; phase: Phase }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 400,
        ...(phase === 'slide'
          ? { animation: 'authGlideCenter 0.8s cubic-bezier(0.4, 0, 0.2, 1) forwards' }
          : phase === 'exit'
            ? { animation: 'authGlideFade 0.6s ease-in forwards' }
            : {}),
      }}
    >
      {/* Animated check circle */}
      <div
        style={{
          width: 80,
          height: 80,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, var(--primary-400), var(--primary-700))',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 32,
          animation: 'authCheckGrow 0.5s cubic-bezier(0.34, 1.56, 0.64, 1), authPulseRing 1.2s ease-out 0.4s',
        }}
      >
        <svg width="36" height="36" viewBox="0 0 32 32" fill="none">
          <path
            d="M8 16.5L13.5 22L24 11"
            stroke="#fff"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeDasharray="48"
            style={{
              strokeDashoffset: 48,
              animation: 'authCheckDraw 0.5s ease-out 0.25s forwards',
            }}
          />
        </svg>
      </div>

      {/* Welcome text */}
      <div
        style={{
          textAlign: 'center',
          animation: 'authTextUp 0.5s ease-out 0.35s both',
        }}
      >
        <h2
          style={{
            fontSize: 28,
            fontWeight: 700,
            color: 'var(--text-primary)',
            fontFamily: 'var(--font-sans)',
            marginBottom: 8,
            letterSpacing: '-0.02em',
          }}
        >
          Welcome back, {name}
        </h2>
        <p
          style={{
            fontSize: 14,
            color: 'var(--text-muted)',
            fontFamily: 'var(--font-sans)',
          }}
        >
          {phase === 'check' && 'Authenticated successfully'}
          {phase === 'slide' && 'Loading your workspace...'}
          {(phase === 'expand' || phase === 'exit') && 'Taking you there now...'}
        </p>
      </div>

      {/* Progress bar */}
      <div
        style={{
          width: 200,
          height: 4,
          borderRadius: 2,
          background: 'var(--surface-0)',
          marginTop: 40,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: '100%',
            height: '100%',
            borderRadius: 2,
            background: 'linear-gradient(90deg, var(--primary-300), var(--primary))',
            transformOrigin: 'left',
            transform: 'scaleX(0)',
            animation: 'authBarFill 2.2s cubic-bezier(0.4, 0, 0.2, 1) forwards',
          }}
        />
      </div>

      {/* Decorative dots */}
      <div
        style={{
          display: 'flex',
          gap: 6,
          marginTop: 24,
          animation: 'authTextUp 0.5s ease-out 0.6s both',
        }}
      >
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: 'var(--primary-200)',
              animation: `authDotPulse 1.2s ease-in-out ${i * 0.2}s infinite`,
            }}
          />
        ))}
        <style>{`
          @keyframes authDotPulse {
            0%, 100% { opacity: 0.3; transform: scale(1); }
            50% { opacity: 1; transform: scale(1.3); }
          }
        `}</style>
      </div>
    </div>
  )
}
